import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Save, AlertCircle, CheckCircle, Loader2, Settings, Zap } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_PROMPT = `Eres un analista experto en clasificación de industrias. Tu tarea es clasificar empresas en las categorías de industria correctas.

Tu rol es:
1. Analizar los datos proporcionados de la empresa (PDFs, archivos Excel, datos CSV)
2. Identificar la industria principal, industria secundaria, sub-industria y modelo de negocio
3. Evaluar el nivel de madurez operacional de la empresa
4. Evaluar la calidad y completitud de los datos
5. Recomendar KPIs relevantes para esta industria específica
6. Sugerir módulos apropiados para habilitar
7. Proporcionar benchmarks específicos de la industria
8. Definir qué DEBE y qué NO DEBE inferirse de los datos

IMPORTANTE: Toda tu respuesta debe estar en ESPAÑOL, incluyendo los valores de clasificación, advertencias y recomendaciones.

Sé preciso, exhaustivo y proporciona scores de confianza para tu clasificación.`;

const IndustryGatewayConfig = () => {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState({
    system_prompt: DEFAULT_PROMPT,
    llm_provider: 'openai',
    llm_model: 'gpt-4o-mini',
    confidence_threshold: 60,
    api_key: '',
    is_active: false
  });

  // Fetch existing config
  const { data: existingConfig, isLoading } = useQuery({
    queryKey: ['industry-gateway-config'],
    queryFn: async () => {
      const response = await axios.get(`${API}/governance/industry-gateway/config`);
      return response.data;
    }
  });
  
  // Load config when data is fetched (React Query v5 compatible)
  useEffect(() => {
    if (existingConfig && existingConfig.system_prompt) {
      setConfig({
        system_prompt: existingConfig.system_prompt || DEFAULT_PROMPT,
        llm_provider: existingConfig.llm_provider || 'openai',
        llm_model: existingConfig.llm_model || 'gpt-4o-mini',
        confidence_threshold: existingConfig.confidence_threshold || 60,
        api_key: existingConfig.api_key || '',
        is_active: existingConfig.is_active || false
      });
    }
  }, [existingConfig]);

  // Save config mutation
  const saveConfigMutation = useMutation({
    mutationFn: async (configData) => {
      const response = await axios.post(`${API}/governance/industry-gateway/config`, configData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['industry-gateway-config']);
      toast.success('Configuración guardada exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    }
  });

  const handleSave = () => {
    if (!config.system_prompt) {
      toast.error('El prompt del sistema es obligatorio');
      return;
    }

    if (config.is_active && !config.api_key) {
      toast.error('Debes proporcionar una API key para activar el agente');
      return;
    }

    saveConfigMutation.mutate(config);
  };

  const handleUseDefaultPrompt = () => {
    setConfig({ ...config, system_prompt: DEFAULT_PROMPT });
    toast.info('Prompt predeterminado cargado');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Configuración del Industry Gateway Agent (GOV-ENT-000)
        </h2>
        <p className="text-slate-400">
          Este agente se ejecuta automáticamente al inicio para clasificar cada tenant por industria.
        </p>
      </div>

      {/* Status Card */}
      <Card className={`glass-dark ${config.is_active ? 'border-green-500/30' : 'border-yellow-500/30'}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {config.is_active ? (
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-yellow-400" />
                </div>
              )}
              <div>
                <p className="text-lg font-semibold text-white">
                  {config.is_active ? 'Agente Activo' : 'Agente Inactivo'}
                </p>
                <p className="text-sm text-slate-400">
                  {config.is_active 
                    ? 'El agente está clasificando automáticamente nuevos tenants' 
                    : 'Configura y activa el agente para comenzar'}
                </p>
              </div>
            </div>
            <Badge className={config.is_active ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}>
              {config.is_active ? 'ACTIVO' : 'INACTIVO'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* LLM Configuration */}
      <Card className="glass-dark border-blue-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuración del LLM
          </CardTitle>
          <CardDescription className="text-slate-400">
            Selecciona el proveedor y modelo de IA para la clasificación
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="llm_provider" className="text-slate-300">Proveedor LLM</Label>
              <Select
                value={config.llm_provider}
                onValueChange={(value) => setConfig({ ...config, llm_provider: value })}
              >
                <SelectTrigger className="form-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="llm_model" className="text-slate-300">Modelo</Label>
              <Select
                value={config.llm_model}
                onValueChange={(value) => setConfig({ ...config, llm_model: value })}
              >
                <SelectTrigger className="form-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {config.llm_provider === 'openai' ? (
                    <>
                      <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                      <SelectItem value="gpt-4o-mini">GPT-4o Mini (Recomendado)</SelectItem>
                      <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="claude-sonnet-4-20250514">Claude Sonnet 4</SelectItem>
                      <SelectItem value="claude-3-haiku-20240307">Claude 3 Haiku</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="api_key" className="text-slate-300">
              API Key (Super Admin) 
              <span className="text-xs text-slate-500 ml-2">
                Opcional - usa Emergent LLM Key si está vacío
              </span>
            </Label>
            <Input
              id="api_key"
              type="password"
              value={config.api_key}
              onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
              className="form-input"
              placeholder="sk-..."
            />
          </div>

          <div>
            <Label htmlFor="threshold" className="text-slate-300">
              Umbral de Confianza Mínimo: {config.confidence_threshold}%
            </Label>
            <input
              id="threshold"
              type="range"
              min="0"
              max="100"
              value={config.confidence_threshold}
              onChange={(e) => setConfig({ ...config, confidence_threshold: parseInt(e.target.value) })}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {config.confidence_threshold < 50 && '⚠️ Muy bajo - Puede generar clasificaciones poco confiables'}
              {config.confidence_threshold >= 50 && config.confidence_threshold < 70 && 'ℹ️ Moderado - Aceptable para la mayoría de casos'}
              {config.confidence_threshold >= 70 && '✅ Alto - Recomendado para máxima precisión'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* System Prompt */}
      <Card className="glass-dark border-purple-500/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Prompt del Sistema
              </CardTitle>
              <CardDescription className="text-slate-400">
                Define cómo el agente debe analizar y clasificar las empresas
              </CardDescription>
            </div>
            <Button
              onClick={handleUseDefaultPrompt}
              variant="outline"
              size="sm"
            >
              Usar Prompt Predeterminado
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Textarea
            value={config.system_prompt}
            onChange={(e) => setConfig({ ...config, system_prompt: e.target.value })}
            className="form-input font-mono text-sm min-h-[300px]"
            placeholder="Escribe el prompt del sistema..."
          />
          <p className="text-xs text-slate-500 mt-2">
            Este prompt guía al LLM sobre cómo clasificar industrias, evaluar madurez y recomendar KPIs.
          </p>
        </CardContent>
      </Card>

      {/* Activation */}
      <Card className="glass-dark border-slate-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white mb-1">Activar Agente</p>
              <p className="text-sm text-slate-400">
                Una vez activado, el agente clasificará automáticamente todos los tenants nuevos
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.is_active}
                onChange={(e) => setConfig({ ...config, is_active: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          onClick={handleSave}
          disabled={saveConfigMutation.isPending}
          className="btn-gradient"
          size="lg"
        >
          {saveConfigMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Guardar Configuración
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default IndustryGatewayConfig;
